import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { MapPin, Check, Plus } from "lucide-react"
import { useAuth } from "@/context/Auth"
import AddressForm from "./address-form"

export default function AddressSelectorModal({ isOpen, onClose, onSelectAddress }) {
  const { getAddresses } = useAuth()
  const [addresses, setAddresses] = useState([])
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    if (isOpen) {
      getAddresses().then(setAddresses)
    }
  }, [isOpen, showForm, getAddresses])

  const handleSelectAddress = () => {
    if (selectedAddress) {
      onSelectAddress(selectedAddress)
      onClose()
    }
  }

  const handleAddAddress = () => setShowForm(true)
  const handleFormCancel = () => setShowForm(false)
  const handleFormSubmit = async () => {
    setShowForm(false)
    // Refresh addresses after adding
    const updated = await getAddresses()
    setAddresses(updated)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Select Shipping Address
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {showForm ? (
            <AddressForm onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
          ) : (
            <>
              {addresses.map((address) => (
                <Card
                  key={address.id}
                  className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedAddress?.id === address.id ? "ring-2 ring-primary bg-primary/5" : "border-gray-200"
                  }`}
                  onClick={() => setSelectedAddress(address)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={address.isDefaultShipping ? "default" : "secondary"}>
                          {address.type || "Shipping"}
                        </Badge>
                        {address.isDefaultShipping && (
                          <Badge variant="outline" className="text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium text-primary">{address.name}</p>
                        <p className="text-sm text-gray-600">{address.address1}</p>
                        {address.address2 && <p className="text-sm text-gray-600">{address.address2}</p>}
                        <p className="text-sm text-gray-600">
                          {address.city}, {address.state} {address.zip}
                        </p>
                        <p className="text-sm text-gray-600">{address.country}</p>
                      </div>
                    </div>
                    {selectedAddress?.id === address.id && (
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </Card>
              ))}

              {/* Add New Address Option */}
              <Card
                className="p-4 border-dashed border-2 border-gray-300 hover:border-primary transition-colors cursor-pointer"
                onClick={handleAddAddress}
              >
                <div className="flex items-center justify-center gap-2 text-gray-500 hover:text-primary">
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Add New Address</span>
                </div>
              </Card>
            </>
          )}
        </div>

        {!showForm && (
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1 bg-transparent">
              Cancel
            </Button>
            <Button onClick={handleSelectAddress} disabled={!selectedAddress} className="flex-1">
              Use Selected Address
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}